import React, { HTMLProps, ReactElement, useContext, useState } from "react";
import { Omit, ReactNode as TypedReactNode } from "../../../../sharedTypes";
import styled from "styled-components";
import { Themes } from "../../../styling/deprecated_theme";
import { ToggleTipProps } from "../../layout/toggle_tip";

// there might be a better way to do this eventually, but for now we need a way to
// make sure each radio group is unique
const groupNames: { [name: string]: number } = {};

export enum RadioGroupDisplay {
  normal,
  buttonGroup,
}

const RadioContext = React.createContext<{
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange(newValue: any): void;
  radioGroupName: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  currentValue?: any;
}>({
  onChange: () => undefined,
  radioGroupName: "",
});

// don't allow onChange listeners on a radio button. this should only be handled by the parent radioGroup
interface RadioButtonProps extends Omit<HTMLProps<HTMLInputElement>, "onChange"> {
  label: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  dataTest?: string;
  toggleTip?: ReactElement<ToggleTipProps>;
}

const StyledRadioGroup = styled.div`
  display: flex;
  flex-direction: column;

  .radio-button {
    display: flex;
    align-items: center;
    padding: ${({ theme }) =>
      theme.VARIATION === Themes.Admin ? undefined : theme.deprecatedSpacing.component.three};

    .rb-input {
      flex: 0 0 20px;
      appearance: none;
      height: 20px;
      width: 20px;
      border: ${({ theme }) =>
        `${theme.deprecatedBorder.weight.heavy} solid ${theme.deprecatedColors.gray.eight}`};
      border-radius: 50%;
      margin: 8px;

      &:checked {
        border: ${({ theme }) =>
          `${theme.deprecatedBorder.weight.heavy} solid ${theme.deprecatedColors.primary.normal}`};
        &:after {
          content: "";
          position: relative;
          height: 10px;
          width: 10px;
          display: block;
          background-color: ${({ theme }) => theme.deprecatedColors.primary.normal};
          border-radius: 50%;
          top: calc(50% - 5px);
          left: calc(50% - 5px);
        }
      }
    }

    .rb-label {
      flex: 1;
      text-transform: none;
      flex-grow: 1;
      font-size: ${({ theme }) =>
        theme.VARIATION === Themes.Admin ? theme.font.size.label.one : theme.font.size.body.one};
    }
  }
`;

const StyledButtonGroup = styled.div`
  display: flex;
  flex-direction: row;
  color: ${({ theme }) => theme.deprecatedColors.primary.normal};

  .radio-button {
    cursor: pointer;
    padding: ${({ theme }) => theme.deprecatedSpacing.component.three};
    text-align: center;
    border-top: ${({ theme }) =>
      `${theme.deprecatedBorder.weight.normal} solid ${theme.deprecatedColors.primary.normal}`};
    border-bottom: ${({ theme }) =>
      `${theme.deprecatedBorder.weight.normal} solid ${theme.deprecatedColors.primary.normal}`};
    border-right: ${({ theme }) =>
      `${theme.deprecatedBorder.weight.normal} solid ${theme.deprecatedColors.primary.normal}`};
    position: relative;

    &:last-child {
      border-top-right-radius: ${({ theme }) => theme.deprecatedBorder.radius.normal};
      border-bottom-right-radius: ${({ theme }) => theme.deprecatedBorder.radius.normal};
      .rb-input {
        border-top-right-radius: ${({ theme }) => theme.deprecatedBorder.radius.normal};
        border-bottom-right-radius: ${({ theme }) => theme.deprecatedBorder.radius.normal};
      }
    }

    &:first-child {
      border-left: ${({ theme }) =>
        `${theme.deprecatedBorder.weight.normal} solid ${theme.deprecatedColors.primary.normal}`};
      border-top-left-radius: ${({ theme }) => theme.deprecatedBorder.radius.normal};
      border-bottom-left-radius: ${({ theme }) => theme.deprecatedBorder.radius.normal};
      .rb-input {
        border-top-left-radius: ${({ theme }) => theme.deprecatedBorder.radius.normal};
        border-bottom-left-radius: ${({ theme }) => theme.deprecatedBorder.radius.normal};
      }
    }

    .rb-input {
      cursor: pointer;
      appearance: none;
      height: 100%;
      width: 100%;
      position: absolute;
      margin: 0;
      top: 0;
      left: 0;
      border-radius: 0;

      background-color: ${({ theme }) => theme.deprecatedColors.white};
      &.selected {
        background-color: ${({ theme }) => theme.deprecatedColors.primary.normal};
      }
    }

    .rb-label {
      cursor: pointer;
      position: relative;
      &.selected {
        color: ${({ theme }) => theme.deprecatedColors.white};
      }
    }
  }
`;

export function RadioButton({
  label,
  value,
  dataTest,
  className,
  toggleTip,
  ...rest
}: RadioButtonProps) {
  const { radioGroupName, currentValue, onChange } = useContext(RadioContext);
  const [focused, setFocused] = useState<boolean>(false);
  const classes = className ? `radio-button ${className}` : "radio-button";
  const selectedClass = value === currentValue ? "selected" : "";
  const focusedClass = focused ? "focus" : "";

  return (
    <label
      className={`${classes} ${selectedClass} ${focusedClass}`}
      data-test={dataTest}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      <input
        name={radioGroupName}
        onChange={({ currentTarget: { checked } }) => {
          if (checked) {
            onChange(value);
          }
        }}
        checked={value === currentValue}
        type="radio"
        value={value}
        {...rest}
        className={`${selectedClass} rb-input`}
      />
      <span className={`${selectedClass} rb-label`}>
        {label} {toggleTip}
      </span>
    </label>
  );
}

const Label = styled.label`
  display: inline-block;
  text-transform: uppercase;
  color: ${({ theme }) => theme.deprecatedColors.gray.five};
  font-size: ${({ theme }) => theme.font.size.label.one};
  padding-bottom: ${({ theme }) => theme.deprecatedSpacing.component.three};
  padding-right: ${({ theme }) => theme.deprecatedSpacing.component.three};
`;

const HelperText = styled.p`
  display: block;
  margin-top: ${({ theme }) => theme.deprecatedSpacing.component.three};
  color: ${({ theme }) => theme.deprecatedColors.gray.six};
  font-size: ${({ theme }) => theme.font.size.body.three};
  line-height: ${({ theme }) => theme.font.size.body.two};
`;

export function RadioGroup<T>({
  name,
  value,
  onChange,
  children,
  label,
  helperText,
  display = RadioGroupDisplay.normal,
  className,
}: {
  name: string;
  value?: T;
  onChange(newValue: T): void;
  children: TypedReactNode<RadioButtonProps>;
  display?: RadioGroupDisplay;
  className?: string;
  role?: string;
  label?: string;
  helperText?: string;
}) {
  if (Object.keys(groupNames).includes(name)) {
    groupNames[name] = groupNames[name] + 1;
  } else {
    groupNames[name] = 1;
  }
  const groupName = `${name}|${groupNames[name]}`;
  return (
    <RadioContext.Provider
      value={{
        onChange,
        currentValue: value,
        radioGroupName: groupName,
      }}
    >
      {display === RadioGroupDisplay.normal && (
        <StyledRadioGroup className={className}>
          {label && <Label htmlFor={name}>{label}</Label>}
          {helperText && <HelperText>{helperText}</HelperText>}
          {children}
        </StyledRadioGroup>
      )}
      {display === RadioGroupDisplay.buttonGroup && (
        <StyledButtonGroup className={className}>
          {label && <Label htmlFor={name}>{label}</Label>}
          {helperText && <HelperText>{helperText}</HelperText>}
          {children}
        </StyledButtonGroup>
      )}
    </RadioContext.Provider>
  );
}
